import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import '../admin.css';
import { adminLogin, adminLogin2fa, adminMe, adminSsoConfig, resolveSso } from '../api/admin';
import type { SsoChoice } from '../api/admin';
import TwoFactorEnroll from '../components/TwoFactorEnroll';
import { AuthSplit, WhatsNew } from './AuthLayout';

/**
 * The one sign-in page for the whole product, at /login.
 *
 * Because the IdP is per tenant, this page cannot know which provider to offer
 * until it knows the email domain — so it asks as you type. Three states result:
 * password + optional SSO button, email-first when a domain requires SSO, and
 * the error slot for a sign-in that came back rejected.
 *
 * R13 (screen 04): the form on the canvas beside "what's new". Where the typed
 * domain has single sign-on, an accent-wash panel names the provider(s) and
 * carries "Continue with …" — above the password button when SSO is optional,
 * in place of the password when it is required. The two-factor steps render
 * in the same column.
 */
export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(true);
  const [ssoEnabled, setSsoEnabled] = useState(false);
  const [ssoLoginUrl, setSsoLoginUrl] = useState('/api/admin/sso/login');

  /**
   * What the typed domain resolves to: the account, and every provider it has
   * designated for team sign-in. More than one is normal for a company that
   * runs, say, Entra for staff and Okta for a subsidiary — so they are all
   * offered rather than guessing.
   */
  const [resolved, setResolved] = useState<{ accountName: string; providers: SsoChoice[] } | null>(null);
  const [ssoRequired, setSsoRequired] = useState(false);
  const [usePassword, setUsePassword] = useState(false);
  /**
   * The two-factor steps a password sign-in can land in (T2): 'code' when
   * this sign-in has 2FA and owes the six digits; 'enroll' when the account
   * requires 2FA and this sign-in has none yet — the forced-enrollment flow,
   * which cannot be skipped because the session refuses everything else.
   */
  const [twoFa, setTwoFa] = useState<{ step: 'code'; challenge: string } | { step: 'enroll' } | null>(null);
  const [code, setCode] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Why this page, when it was not asked for.
   *
   * A session that times out sends the tab here (shared/session.ts) rather
   * than leaving it on a screen that has stopped saving. It arrives with the
   * reason and with where it was, and both are held in state: the history
   * entry is cleared below so a reload does not announce a timeout that
   * happened yesterday.
   */
  const arrival = (location.state as { notice?: string; from?: string } | null) || null;
  const [notice] = useState(arrival?.notice || '');
  const [from] = useState(arrival?.from || '');
  /**
   * A file link past its lifetime sends a visitor here with `?next=` (finding
   * 96). Only ever one of our own file paths: the server serves it, so it is a
   * full navigation, and anything else in that parameter is ignored.
   */
  const [fileNext] = useState(() => {
    const next = new URLSearchParams(location.search).get('next') || '';
    return /^\/uploads\/[^/?#]+\/[^/?#]+$/.test(next) ? next : '';
  });
  /** Where a signed-in user goes: back to the file, else their own home. */
  const land = (isPlatformAdmin: boolean, masquerading: boolean) => {
    if (fileNext) {
      window.location.assign(fileNext);
      // A file the browser shows replaces this page. One it downloads does
      // not, and would leave a signed-in person looking at the sign-in form.
      window.setTimeout(() => navigate(routeFor(isPlatformAdmin, masquerading), { replace: true }), 1500);
      return;
    }
    navigate(routeFor(isPlatformAdmin, masquerading), { replace: true });
  };

  /** Signed-in users land where they belong: staff on the console, customers on their forms. */
  const routeFor = (isPlatformAdmin: boolean, masquerading: boolean) => {
    const home = isPlatformAdmin && !masquerading ? '/platform' : '/admin';
    // Back to the screen the timeout interrupted — but only if it is a screen
    // this sign-in can actually open, so a customer does not land on /platform
    // and a staff member does not land in an account they have left.
    return from.startsWith(home) ? from : home;
  };

  useEffect(() => {
    adminMe()
      .then((r) => land(r.isPlatformAdmin, r.masquerading))
      .catch(() => undefined)
      .finally(() => setChecking(false));
    adminSsoConfig()
      .then((r) => { setSsoEnabled(!!r.enabled); if (r.loginUrl) setSsoLoginUrl(r.loginUrl); })
      .catch(() => undefined);
    try {
      const err = new URLSearchParams(window.location.search).get('sso_error');
      if (err) { setError(err); window.history.replaceState({}, '', window.location.pathname); }
    } catch { /* ignore */ }
    // The reason has been read off the entry; a reload should not replay it.
    if (arrival) navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced: one lookup after typing stops, not one per keystroke.
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    const domain = email.split('@')[1];
    if (!domain || !domain.includes('.')) { setResolved(null); setSsoRequired(false); return; }
    timer.current = setTimeout(() => {
      resolveSso(email)
        .then((r) => {
          setResolved(r.providers?.length ? { accountName: r.accountName || '', providers: r.providers } : null);
          setSsoRequired(!!r.ssoRequired);
        })
        .catch(() => { setResolved(null); setSsoRequired(false); });
    }, 350);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [email]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    // Email-first: the button takes them to the IdP rather than posting a
    // password. With several providers there is nothing to submit — the
    // buttons below are the form.
    if (ssoRequired && resolved && !usePassword) {
      if (resolved.providers.length === 1) window.location.href = resolved.providers[0].loginUrl;
      return;
    }
    setError(''); setBusy(true);
    try {
      const r = await adminLogin(email.trim(), password);
      if (r.twoFactor && r.challenge) { setTwoFa({ step: 'code', challenge: r.challenge }); return; }
      if (r.twoFactorSetup) { setTwoFa({ step: 'enroll' }); return; }
      await goIn();
    } catch (err: any) {
      setError(err.message || 'Login failed');
    } finally {
      setBusy(false);
    }
  }

  /** The signed-in landing, shared by every path that ends with a session. */
  async function goIn() {
    const me = await adminMe();
    land(me.isPlatformAdmin, me.masquerading);
  }

  async function onSubmitCode(e: React.FormEvent) {
    e.preventDefault();
    if (twoFa?.step !== 'code' || !code.trim()) return;
    setError(''); setBusy(true);
    try {
      await adminLogin2fa(twoFa.challenge, code.trim());
      await goIn();
    } catch (err: any) {
      // An expired challenge means back to the password, not a dead end.
      if (err?.body?.code === 'challenge_expired') { setTwoFa(null); setCode(''); }
      setError(err.message || 'That code did not work.');
    } finally {
      setBusy(false);
    }
  }

  /** Out of the code step without waiting for the challenge to lapse: back to the password, email kept. */
  function startOver() {
    setTwoFa(null); setCode(''); setPassword(''); setError('');
  }

  const emailFirst = ssoRequired && !!resolved && !usePassword;
  const domain = email.split('@')[1] || '';
  const choices = resolved?.providers || [];
  const one = choices.length === 1 ? choices[0] : null;
  /** "Okta", "Okta or Entra", "Okta, Entra or Google" */
  const providerNames = choices.map((p) => p.name).reduce((s, n, i, all) =>
    (i === 0 ? n : `${s}${i === all.length - 1 ? ' or ' : ', '}${n}`), '');
  /** SSO offered beside a password: the typed domain's providers (the panel), or the app's own (a button below). */
  const optionalSso = !emailFirst && !!resolved;
  const appSso = !emailFirst && !resolved && ssoEnabled;

  let body: React.ReactNode;
  if (checking) {
    body = <p className="auth-lede">Loading…</p>;
  } else if (twoFa?.step === 'code') {
    // The second-factor step: same column, different question.
    body = (
      <form className="auth-stack" onSubmit={onSubmitCode}>
        <h1 className="auth-title">Enter your code</h1>
        <p className="auth-lede">Enter the six-digit code from your authenticator app.</p>
        {error && <div className="auth-err" role="alert">{error}</div>}
        <div className="auth-field">
          <label htmlFor="login-code">Code</label>
          <input
            id="login-code" className="code" inputMode="numeric" autoComplete="one-time-code" value={code}
            autoFocus onChange={(e) => setCode(e.target.value)} placeholder="123 456"
          />
        </div>
        <button className="fs-btn ink lg block" type="submit" disabled={busy}>
          {busy ? 'Checking…' : 'Sign in'}
        </button>
        <p className="auth-fine">Lost your phone? Enter one of your recovery codes instead.</p>
        <div className="auth-links">
          <button type="button" className="auth-link" onClick={startOver}>← Use a different account</button>
        </div>
      </form>
    );
  } else if (twoFa?.step === 'enroll') {
    // Forced enrollment: this account requires two-factor, and this sign-in
    // has none yet. The session refuses everything else until it is set up.
    body = (
      <div className="auth-stack">
        <h1 className="auth-title">Set up two-factor</h1>
        <p className="auth-lede">Your team requires two-factor authentication, so it needs setting up before you continue.</p>
        <TwoFactorEnroll onDone={() => { void goIn(); }} />
      </div>
    );
  } else {
    body = (
      <form className="auth-stack" onSubmit={onSubmit}>
        <h1 className="auth-title">Sign in</h1>
        <p className="auth-lede">
          {emailFirst
            ? "Enter your work email and we'll take you to your company's sign-in."
            : 'Sign in to build and manage your forms.'}
        </p>
        {error && <div className="auth-err" role="alert">{error}</div>}
        {!error && notice && <div className="auth-note warn" role="status">{notice}</div>}

        <div className="auth-field">
          <label htmlFor="login-email">Work email</label>
          <input
            id="login-email"
            type="text" inputMode="email" value={email} autoFocus autoComplete="username"
            placeholder="you@company.com"
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        {emailFirst ? (
          <div className="auth-sso">
            <span>
              <b>{domain}</b> uses single sign-on. You'll finish signing in at{' '}
              {one ? one.name : `${resolved!.accountName || 'your company'}`}.
            </span>
            {one ? (
              <button className="fs-btn primary block" type="submit">Continue with {one.name}</button>
            ) : (
              /* Several providers serve this domain, so the person picks. */
              choices.map((p) => (
                <a key={p.slug} className="fs-btn primary block" href={p.loginUrl}>Continue with {p.name}</a>
              ))
            )}
            <button type="button" className="auth-link" onClick={() => setUsePassword(true)}>
              Use a break-glass password instead
            </button>
          </div>
        ) : (
          <div className="auth-field">
            <label htmlFor="login-password">Password</label>
            <input
              id="login-password"
              type="password" value={password} autoComplete="current-password"
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
        )}

        {/* SSO offered but not required: the password stays, and the panel
            offers each of the domain's providers above its button. */}
        {optionalSso && (
          <div className="auth-sso">
            <span>{resolved!.accountName || domain} signs in with <b>{providerNames}</b>.</span>
            {choices.map((p) => (
              <a key={p.slug} className="fs-btn primary block" href={p.loginUrl}>Continue with {p.name}</a>
            ))}
          </div>
        )}

        {!emailFirst && (
          <button className={`fs-btn ${optionalSso ? 'neutral' : 'ink'} lg block`} type="submit" disabled={busy}>
            {busy ? 'Signing in…' : 'Sign in with password'}
          </button>
        )}

        {appSso && (
          <>
            <div className="auth-or">or</div>
            <a className="fs-btn neutral lg block" href={ssoLoginUrl}>Sign in with SSO</a>
          </>
        )}

        <div className="auth-links">
          <span>New here? <Link to="/pricing">Create an account</Link></span>
          {/* Only where a password is what gets you in. On the email-first path
              there is no password to have forgotten — the IdP holds it. */}
          {!emailFirst && <Link to="/forgot-password">Forgot password?</Link>}
        </div>
      </form>
    );
  }

  return <AuthSplit aside={<WhatsNew />}>{body}</AuthSplit>;
}
